import type { WorldcupResearchCoverageQuery, WorldcupResearchFacetsQuery, WorldcupResearchQuery } from '~/features/worldcup-research/types'

import { queryOptions } from '@tanstack/react-query'
import {
  getPublicResearchHeadToHead,
  getPublicResearchMatchDetail,
  getPublicResearchTeamProfile,
  listPublicResearchCoverage,
  listPublicResearchFacets,
  listPublicResearchTeamMatchStats,
  searchPublicResearchTeamMatchStatsEvidence,
} from './api'

export const initialPublicResearchFilters: WorldcupResearchQuery = {
  competitionCode: 'world_cup',
  researchView: 'major_tournament',
  minDataQualityScore: 0.65,
  limit: 50,
  offset: 0,
  sortDirection: 'asc',
}

export const nationalTeamProfileDefaultFilters: WorldcupResearchQuery = {
  ...initialPublicResearchFilters,
  competitionCode: 'world_cup_qualifier',
  competitionCodes: ['world_cup_qualifier'],
  researchView: 'exploratory_all',
  minDataQualityScore: 0,
  limit: 100,
}

export const initialPublicResearchCoverageFilters: WorldcupResearchCoverageQuery = {
  competitionCode: 'world_cup',
  researchView: 'major_tournament',
  minDataQualityScore: 0.65,
  coverageLimit: 50,
  coverageOffset: 0,
  gapLimit: 50,
  gapOffset: 0,
}

export const initialPublicResearchFacetFilters: WorldcupResearchFacetsQuery = {
  researchView: 'major_tournament',
  minDataQualityScore: 0.65,
}

export const publicResearchQueryKeys = {
  all: ['public-research'] as const,
  teamMatchStats: (filters: WorldcupResearchQuery = initialPublicResearchFilters) =>
    [...publicResearchQueryKeys.all, 'team-match-stats', filters] as const,
  coverage: (filters: WorldcupResearchCoverageQuery = initialPublicResearchCoverageFilters) =>
    [...publicResearchQueryKeys.all, 'coverage', filters] as const,
  facets: (filters: WorldcupResearchFacetsQuery = initialPublicResearchFacetFilters) =>
    [...publicResearchQueryKeys.all, 'facets', filters] as const,
  evidenceSearch: (filters: WorldcupResearchQuery = initialPublicResearchFilters) =>
    [...publicResearchQueryKeys.all, 'evidence-search-team-match-stats', filters] as const,
  teamProfile: (teamSlug: string, filters: WorldcupResearchQuery = initialPublicResearchFilters) =>
    [...publicResearchQueryKeys.all, 'team-profile', teamSlug, filters] as const,
  headToHead: (filters: WorldcupResearchQuery & { team: string, opponent: string }) =>
    [...publicResearchQueryKeys.all, 'head-to-head', filters] as const,
  matchDetail: (matchId: string) => [...publicResearchQueryKeys.all, 'match-detail', matchId] as const,
}

export function publicResearchTeamMatchStatsQueryOptions(filters: WorldcupResearchQuery = initialPublicResearchFilters) {
  return queryOptions({
    queryKey: publicResearchQueryKeys.teamMatchStats(filters),
    queryFn: () => listPublicResearchTeamMatchStats(filters),
  })
}

export function publicResearchCoverageQueryOptions(filters: WorldcupResearchCoverageQuery = initialPublicResearchCoverageFilters) {
  return queryOptions({
    queryKey: publicResearchQueryKeys.coverage(filters),
    queryFn: () => listPublicResearchCoverage(filters),
  })
}

export function publicResearchFacetsQueryOptions(filters: WorldcupResearchFacetsQuery = initialPublicResearchFacetFilters) {
  return queryOptions({
    queryKey: publicResearchQueryKeys.facets(filters),
    queryFn: () => listPublicResearchFacets(filters),
  })
}

export function publicResearchEvidenceSearchQueryOptions(filters: WorldcupResearchQuery = initialPublicResearchFilters) {
  return queryOptions({
    queryKey: publicResearchQueryKeys.evidenceSearch(filters),
    queryFn: () => searchPublicResearchTeamMatchStatsEvidence(filters),
  })
}

export function publicResearchTeamProfileQueryOptions(teamSlug: string, filters: WorldcupResearchQuery = initialPublicResearchFilters) {
  return queryOptions({
    queryKey: publicResearchQueryKeys.teamProfile(teamSlug, filters),
    queryFn: () => getPublicResearchTeamProfile(teamSlug, filters),
  })
}

export function publicResearchHeadToHeadQueryOptions(filters: WorldcupResearchQuery & { team: string, opponent: string }) {
  return queryOptions({
    queryKey: publicResearchQueryKeys.headToHead(filters),
    queryFn: () => getPublicResearchHeadToHead(filters),
  })
}

export function publicResearchMatchDetailQueryOptions(matchId: string) {
  return queryOptions({
    queryKey: publicResearchQueryKeys.matchDetail(matchId),
    queryFn: () => getPublicResearchMatchDetail(matchId),
  })
}
